import { CheckCircle2 } from "lucide-react";
import { Reveal, SectionHead, Counter } from "@/components/Reveal";

const EXPERTISE = [
  "Optical Fiber Infrastructure",
  "Telecom Equipment Supply",
  "Underground OFC Deployment",
  "Aerial OFC Deployment",
  "Network Infrastructure",
  "Fiber Leasing",
  "Co-location",
  "RF Connectivity",
  "Shifting & Relocation",
  "Civil Works",
  "Telecom Infrastructure Support",
];

const STATS = [
  { value: <Counter to={24} />, suffix: "/7", label: "Support Availability" },
  { value: <Counter to={365} />, suffix: "", label: "Days a Year" },
  { value: "6F–96F", suffix: "", label: "OFC Solutions" },
  { value: "End-to-End", suffix: "", label: "Infrastructure Support" },
];

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <SectionHead
              eyebrow="About JAYTRIKA"
              title="Engineering Connectivity. Delivering Reliability."
            />
            <Reveal delay={0.2}>
              <p className="mt-6 text-base leading-relaxed text-slate-600">
                JAYTRIKA is a professional telecom infrastructure and equipment supply
                company based in Manikonda, Hyderabad. We specialize in telecom equipment
                supply, optical fiber infrastructure, network equipment, civil works and
                connectivity services — supporting customers from equipment procurement
                through infrastructure deployment and connectivity requirements.
              </p>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                By combining equipment supply, fiber infrastructure, civil works and
                network connectivity under one platform, we give telecom operators,
                enterprises and infrastructure organizations a single, accountable
                technical partner.
              </p>
            </Reveal>
            <Reveal delay={0.28}>
              <div className="mt-8 flex flex-wrap gap-2.5">
                {EXPERTISE.map((item) => (
                  <span
                    key={item}
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3.5 py-1.5 text-xs font-medium text-slate-700 transition-colors duration-200 hover:border-blue-300 hover:bg-blue-50"
                  >
                    <CheckCircle2 size={13} className="text-electric" />
                    {item}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="relative">
            <div className="relative overflow-hidden rounded-3xl ring-1 ring-slate-900/10 shadow-2xl shadow-blue-900/20">
              <img
                src="https://images.unsplash.com/photo-1594915440248-1e419eba6611?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHw0fHxmaWJlciUyMG9wdGljJTIwY2FibGUlMjBuZW9uJTIwbmV0d29ya2luZ3xlbnwwfHx8fDE3OTExMjA2NDB8MA&ixlib=rb-4.1.0&q=85"
                alt="Optical fiber strands carrying light, representing JAYTRIKA fiber infrastructure"
                className="aspect-[4/3] w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/10 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between rounded-2xl border border-white/15 bg-navy/70 px-5 py-4 backdrop-blur-md">
                <div>
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-cyan-300">
                    Manikonda · Hyderabad
                  </p>
                  <p className="mt-1 text-sm font-semibold text-white">
                    Connecting Today. Building Tomorrow.
                  </p>
                </div>
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-60" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-cyan-400" />
                </span>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-6">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div
                data-testid={`about-stat-${i}`}
                className="group rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-blue-400/50 hover:bg-white hover:shadow-xl hover:shadow-blue-500/10"
              >
                <p className="font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
                  {s.value}
                  {s.suffix && <span className="text-electric">{s.suffix}</span>}
                </p>
                <p className="mt-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {s.label}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
